import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getAccessToken,
  validateEmployerSession,
  logout as authLogout,
} from "../services/auth";
import { setAuthToken } from "../services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  const refreshSession = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setAuthToken(null);
      setAuthenticated(false);
      setReady(true);
      return false;
    }

    setAuthToken(token);
    const valid = await validateEmployerSession();
    setAuthenticated(valid);
    setReady(true);
    return valid;
  }, []);

  useEffect(() => {
    refreshSession().catch(() => {
      setAuthenticated(false);
      setReady(true);
    });
  }, [refreshSession]);

  const markAuthenticated = useCallback(() => {
    setAuthenticated(true);
    setReady(true);
  }, []);

  const logout = useCallback(() => {
    authLogout();
    setAuthenticated(false);
  }, []);

  const value = useMemo(
    () => ({
      ready,
      authenticated,
      refreshSession,
      markAuthenticated,
      logout,
    }),
    [ready, authenticated, refreshSession, markAuthenticated, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
